/**
 * Music theory primitives shared by server (transposition, seeding) and
 * web (player rendering, pitch feedback).
 */
export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const A4_MIDI = 69;
export const A4_HZ = 440;
/** MIDI note number -> scientific pitch name, e.g. 60 -> "C4". */
export function midiToName(midi) {
    const rounded = Math.round(midi);
    const name = NOTE_NAMES[((rounded % 12) + 12) % 12];
    const octave = Math.floor(rounded / 12) - 1;
    return `${name}${octave}`;
}
/** Scientific pitch name -> MIDI note number, e.g. "C4" -> 60. Throws on garbage. */
export function nameToMidi(name) {
    const m = /^([A-Ga-g])([#b]?)(-?\d+)$/.exec(name.trim());
    if (!m)
        throw new Error(`Invalid note name: ${name}`);
    const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
    let semis = base[m[1].toUpperCase()];
    if (m[2] === '#')
        semis += 1;
    if (m[2] === 'b')
        semis -= 1;
    return (parseInt(m[3], 10) + 1) * 12 + semis;
}
export function midiToFreq(midi) {
    return A4_HZ * Math.pow(2, (midi - A4_MIDI) / 12);
}
/** Frequency in Hz -> fractional MIDI number. */
export function freqToMidi(freq) {
    return A4_MIDI + 12 * Math.log2(freq / A4_HZ);
}
/** Signed cent distance between a sung (fractional) midi value and a target note. */
export function centsOff(sungMidi, targetMidi) {
    return (sungMidi - targetMidi) * 100;
}
/** Common voice-type reference ranges (approximate, midi note numbers). */
export const VOICE_TYPES = {
    soprano: { low: nameToMidi('C4'), high: nameToMidi('C6') },
    mezzo: { low: nameToMidi('A3'), high: nameToMidi('A5') },
    alto: { low: nameToMidi('F3'), high: nameToMidi('F5') },
    tenor: { low: nameToMidi('C3'), high: nameToMidi('C5') },
    baritone: { low: nameToMidi('G2'), high: nameToMidi('G4') },
    bass: { low: nameToMidi('E2'), high: nameToMidi('E4') },
};
/** Suggest a voice type label from a detected range (closest by midpoint). */
export function suggestVoiceType(lowMidi, highMidi) {
    const mid = (lowMidi + highMidi) / 2;
    let best = 'baritone';
    let bestDist = Infinity;
    for (const [vt, r] of Object.entries(VOICE_TYPES)) {
        const d = Math.abs((r.low + r.high) / 2 - mid);
        if (d < bestDist) {
            bestDist = d;
            best = vt;
        }
    }
    return best;
}
//# sourceMappingURL=music.js.map